import { useEffect, useRef, useState } from 'react';

import { useApi } from '@backstage/core-plugin-api';

import { GithubOrgOption, githubOrgsApiRef } from './GithubOrgsApi';

export function toError(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }
  if (typeof error === 'string') {
    return new Error(error);
  }
  if (
    typeof error === 'number' ||
    typeof error === 'boolean' ||
    typeof error === 'bigint'
  ) {
    return new Error(String(error));
  }
  if (error === null || error === undefined) {
    return new Error('Unknown error');
  }
  try {
    return new Error(JSON.stringify(error));
  } catch {
    return new Error('Unknown error');
  }
}

/**
 * Hook to fetch GitHub organizations with loading and error states.
 */
export function useGithubOrgs(host?: string, refreshTrigger?: number) {
  const githubOrgsApi = useApi(githubOrgsApiRef);
  const [orgs, setOrgs] = useState<GithubOrgOption[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const requestIdRef = useRef<number>(0);
  const isRequestInFlightRef = useRef<boolean>(false);

  useEffect(() => {
    if (!host) {
      setError(
        new Error(
          'GitHub host not configured. Please provide host in ui:options',
        ),
      );
      return (): void => {};
    }

    if (isRequestInFlightRef.current) {
      return (): void => {};
    }

    let isMounted = true;
    const currentRequestId = ++requestIdRef.current;
    isRequestInFlightRef.current = true;
    const requestIdAtStart = requestIdRef.current;

    const run = async () => {
      setLoading(true);
      setError(undefined);
      try {
        const refresh = refreshTrigger !== undefined && refreshTrigger > 0;
        const data = await githubOrgsApi.getOrgs(host, refresh);

        if (isMounted && currentRequestId === requestIdRef.current) {
          setOrgs(data.orgs);
        }
      } catch (e: unknown) {
        if (isMounted && currentRequestId === requestIdRef.current) {
          setError(toError(e));
          setOrgs([]);
        }
      } finally {
        if (isMounted && currentRequestId === requestIdRef.current) {
          setLoading(false);
          isRequestInFlightRef.current = false;
        }
      }
    };

    run();

    return (): void => {
      isMounted = false;
      if (currentRequestId === requestIdAtStart) {
        isRequestInFlightRef.current = false;
      }
    };
  }, [host, githubOrgsApi, refreshTrigger]);

  return { orgs, loading, error };
}

export function validateGithubOrg(
  value: string,
  orgs: GithubOrgOption[],
): boolean {
  if (!value) {
    return false;
  }
  return orgs.some(org => org.value === value);
}

export type { GithubOrgOption, GithubOrgsResponse } from './GithubOrgsApi';
